import React, { useId, useState } from 'react';
import styles from './ChipInput.module.css';

export function ChipInput({ 
  value = [], 
  onChange, 
  placeholder = 'Type and press Enter',
  label,
  required = false,
  maxItems,
  validate,
  error,
  help,
  disabled = false,
}) {
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const inputId = useId();
  const helpId = help ? `${inputId}-help` : undefined;
  const errorId = inputError || error ? `${inputId}-error` : undefined;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addChip();
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeChip(value.length - 1);
    }
  };

  const addChip = () => {
    const trimmedValue = inputValue.trim();
    
    if (!trimmedValue) return;
    
    if (maxItems && value.length >= maxItems) {
      setInputError(`Maximum ${maxItems} items allowed`);
      return;
    }
    
    if (value.includes(trimmedValue)) {
      setInputError('This item already exists');
      return;
    }
    
    if (validate) {
      const validationError = validate(trimmedValue);
      if (validationError) {
        setInputError(validationError);
        return;
      }
    }
    
    onChange([...value, trimmedValue]);
    setInputValue('');
    setInputError('');
  };

  const removeChip = (index) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
  };

  return (
    <div className={styles.container}>
      {label && (
        <label className={styles.label} htmlFor={inputId}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      
      <div className={`${styles.chipInput} ${error ? styles.error : ''}`}>
        <div className={styles.chips}>
          {value.map((chip, index) => (
            <div key={index} className={styles.chip}>
              <span className={styles.chipText}>{chip}</span>
              {!disabled ? (
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => removeChip(index)}
                  aria-label={`Remove ${chip}`}
                >
                  ×
                </button>
              ) : null}
            </div>
          ))}
          <input
            id={inputId}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setInputError('');
            }}
            onKeyDown={handleKeyDown}
            onBlur={addChip}
            placeholder={value.length === 0 ? placeholder : ''}
            className={styles.input}
            disabled={disabled || Boolean(maxItems && value.length >= maxItems)}
            required={required && value.length === 0}
            aria-invalid={Boolean(inputError || error) || undefined}
            aria-describedby={[errorId, helpId].filter(Boolean).join(' ') || undefined}
          />
        </div>
      </div>
      
      {help && !inputError && !error && (
        <p className={styles.help} id={helpId}>{help}</p>
      )}
      {inputError && <p className={styles.errorMessage} id={errorId} role="alert">{inputError}</p>}
      {!inputError && error && <p className={styles.errorMessage} id={errorId} role="alert">{error}</p>}
    </div>
  );
}
